import { useCallback } from 'react'
import isNil from 'lodash/isNil'
import { ECOMM_ITEM_NAME } from 'public/components/default_template/online_ordering/order/OrderItems'
import { getAppliedFundedOffersTotal } from 'public/components/default_template/offers/appliedFundedOffers'
import { sumByField } from 'public/components/online_ordering/reducerUtils'
import { Cart } from 'public/components/online_ordering/types'
import { t } from 'src/translations'
import { CompletedOrder, DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import {
  CartOrder,
  CartSelections,
  WrappedModifier
} from 'public/components/online_ordering/types'
import { MenuItem } from '../../menu_section/MenuSection'

/**
 * Stable string signature of cart selections for use as a refetch trigger
 * (e.g. validateCart). Includes guid and quantity so any change triggers refetch.
 */
export const getCartSelectionsSignature = (
  selections?: CartSelections | null
): string =>
  [
    ...new Set(
      selections
        ?.filter((s): s is NonNullable<typeof s> => s != null)
        ?.map((s) => `${s.guid ?? ''}`)
    )
  ]
    .sort((a, b) => a.localeCompare(b))
    .join('|') ?? ''

const toDollarFormatFixNumber = (number: Number | undefined | null) =>
  number ? +(+number).toFixed(2) : 0

export interface CartLineItems {
  subtotalAmount?: number
  promoCodeAmount?: number
  deliveryChargeTotalAmount?: number
  nonDeliveryNonGratuityNonUbpServiceCharges?: number
  gratuityServiceCharges?: number
  processingServiceCharges?: number
  tax?: number
  tipAmount?: number
  totalOrderAmount?: number
  fundraisingAmount?: number
}

export const calculateDiscountsTotal = (
  globalRewardAmount: number,
  boostOfferAmount: number | undefined,
  discountsTotal: number | undefined
) => (globalRewardAmount || discountsTotal || 0) + (boostOfferAmount || 0)

export const useCartLineItems = (cart: Cart) => {
  return useCallback(
    (
      tip: number | undefined | null,
      fundraisingAmount: number | undefined | null
    ) => {
      const hasTip = !isNil(tip)
      const hasFundraisingAmount =
        !isNil(fundraisingAmount) && fundraisingAmount > 0
      const displayTip = hasTip ? tip : 0
      let displayTotal = cart?.order?.totalV2 || 0
      let displaySubTotal = cart?.order?.preDiscountItemsSubtotal || 0

      if (hasTip) {
        displayTotal = displayTotal + tip
      }

      if (hasFundraisingAmount) {
        displayTotal = displayTotal + fundraisingAmount
      }

      const {
        restaurantDiscount,
        loyaltyDiscounts = [],
        globalReward
      } = cart?.order?.discounts || {}
      const globalRewardAmount = globalReward?.amount || 0
      const hasGlobalReward = Boolean(globalReward)
      const showPromoAmount =
        Boolean(restaurantDiscount) || Boolean(globalReward)

      const showLoyaltyAmount = loyaltyDiscounts?.length

      // if a discount has been applied, calculate the displayed subtotal
      // based on the selections' pre discount price
      if (showPromoAmount || showLoyaltyAmount) {
        displaySubTotal = sumByField('preDiscountPrice')(
          cart?.order?.selections
        )
      }

      const fundedOffersTotal = getAppliedFundedOffersTotal(
        cart?.appliedFundedOffers
      )

      // since global rewards are not applied to the order, we must do this calculation here
      if (hasGlobalReward) {
        displayTotal = displayTotal - globalRewardAmount
      }

      // Not ideal, but funded offers aren't priced with the order so we have to do the math here
      if (fundedOffersTotal) {
        displayTotal = displayTotal - fundedOffersTotal
      }

      const totals: CartLineItems = {
        subtotalAmount: toDollarFormatFixNumber(displaySubTotal),
        tipAmount: toDollarFormatFixNumber(displayTip),
        fundraisingAmount: toDollarFormatFixNumber(fundraisingAmount),
        totalOrderAmount: toDollarFormatFixNumber(displayTotal)
      }

      if (showPromoAmount) {
        // Every funded offer, free delivery included: the Apple Pay sheet's line
        // items have to reconcile with the total charged.
        const realDiscountsTotal = calculateDiscountsTotal(
          globalRewardAmount,
          fundedOffersTotal,
          cart?.order?.discountsTotal
        )
        totals.promoCodeAmount = toDollarFormatFixNumber(-realDiscountsTotal)
      }
      if (
        (cart?.order?.deliveryServiceCharges || 0) > 0 ||
        cart?.diningOptionBehavior === DiningOptionBehavior.Delivery
      ) {
        totals.deliveryChargeTotalAmount = toDollarFormatFixNumber(
          cart?.order?.deliveryServiceCharges
        )
      }
      if (cart?.order?.nonDeliveryNonGratuityNonUbpServiceCharges) {
        totals.nonDeliveryNonGratuityNonUbpServiceCharges =
          toDollarFormatFixNumber(
            cart?.order.nonDeliveryNonGratuityNonUbpServiceCharges
          )
      }
      if (cart?.order?.gratuityServiceCharges) {
        totals.gratuityServiceCharges = toDollarFormatFixNumber(
          cart?.order.gratuityServiceCharges
        )
      }
      if (cart?.order?.processingServiceCharges) {
        totals.processingServiceCharges = toDollarFormatFixNumber(
          cart?.order.processingServiceCharges
        )
      }
      if ((cart?.order?.taxV2 || 0) > 0) {
        totals.tax = toDollarFormatFixNumber(cart?.order?.taxV2)
      }

      return totals
    },
    [cart]
  )
}

export const useApplePayLineItems = ({
  cart,
  showTip,
  showTotal,
  deliveryChargeLabel = t('cart-utils.delivery-cost')
}: any) => {
  const getCartLineItems = useCartLineItems(cart)
  return useCallback(
    (
      tip: number | undefined | null,
      fundraisingAmount: number | undefined | null,
      appliedGiftCardAmount: number | undefined | null,
      appliedToastCashAmount: number | undefined | null
    ) => {
      const cartTotals: CartLineItems = getCartLineItems(tip, fundraisingAmount)
      const loyaltyDiscounts = cart?.order?.discounts?.loyaltyDiscounts || []

      const totals = [
        {
          label: t('cart-utils.subtotal'),
          amount: cartTotals.subtotalAmount,
          id: 'subtotal-amount'
        }
      ]
      const feesExist =
        (cartTotals.gratuityServiceCharges || 0) > 0 ||
        (cartTotals.processingServiceCharges || 0) > 0 ||
        (cartTotals.nonDeliveryNonGratuityNonUbpServiceCharges || 0) > 0
      const taxLabel = feesExist
        ? t('cart-utils.taxes-fees')
        : t('cart-utils.tax')
      const taxId = feesExist ? 'tax-and-fees-amount' : 'tax-amount'
      if ((cartTotals.tax || 0) > 0 || feesExist) {
        let taxesAndFees = cartTotals.tax ? cartTotals.tax : 0
        if (cartTotals.nonDeliveryNonGratuityNonUbpServiceCharges) {
          taxesAndFees += cartTotals.nonDeliveryNonGratuityNonUbpServiceCharges
        }
        if (cartTotals.gratuityServiceCharges) {
          taxesAndFees += cartTotals.gratuityServiceCharges
        }
        if (cartTotals.processingServiceCharges) {
          taxesAndFees += cartTotals.processingServiceCharges
        }
        totals.push({
          label: taxLabel,
          amount: toDollarFormatFixNumber(taxesAndFees),
          id: taxId
        })
      }
      if (cartTotals.promoCodeAmount) {
        totals.push({
          label: t('cart-utils.promo-applied'),
          amount: cartTotals.promoCodeAmount,
          id: 'promo-code-amount'
        })
      }
      if (cartTotals.deliveryChargeTotalAmount) {
        totals.push({
          label: deliveryChargeLabel,
          amount: cartTotals.deliveryChargeTotalAmount,
          id: 'delivery-charge-total-amount'
        })
      }
      if (showTip) {
        totals.push({
          label: t('cart-utils.tip'),
          amount: toDollarFormatFixNumber(cartTotals.tipAmount),
          id: 'tip-amount'
        })
      }
      if (!isNil(fundraisingAmount) && fundraisingAmount > 0) {
        totals.push({
          label: t('cart-utils.support'),
          amount: toDollarFormatFixNumber(cartTotals.fundraisingAmount),
          id: 'fundraising-amount'
        })
      }
      if (!isNil(appliedToastCashAmount) && appliedToastCashAmount > 0) {
        totals.push({
          label: t('cart-utils.toast-cash'),
          amount: toDollarFormatFixNumber(-1 * appliedToastCashAmount),
          id: 'toast-cash-amount'
        })
      }
      if (!isNil(appliedGiftCardAmount) && appliedGiftCardAmount > 0) {
        totals.push({
          label: t('cart-utils.gift-card'),
          amount: toDollarFormatFixNumber(-1 * appliedGiftCardAmount),
          id: 'restaurant-gift-card-amount'
        })
      }
      if (loyaltyDiscounts.length > 0) {
        totals.push({
          label: t('cart-utils.loyalty-discount'),
          amount: toDollarFormatFixNumber(
            -1 * sumByField('amount')(loyaltyDiscounts)
          ),
          id: 'loyalty-discounts'
        })
      }
      if (showTotal) {
        totals.push({
          label: t('cart-utils.total'),
          amount: toDollarFormatFixNumber(cartTotals.totalOrderAmount),
          id: 'total-order-amount'
        })
      }

      return totals
    },
    [
      cart?.order?.discounts?.loyaltyDiscounts,
      deliveryChargeLabel,
      getCartLineItems,
      showTip,
      showTotal
    ]
  )
}

export const getEcommShippingFee = (cart?: Cart | null) =>
  cart?.order?.selections?.find(
    (selection) => selection?.name === ECOMM_ITEM_NAME
  )
export const getCheckoutEligibleItems = (cart?: Cart | null) =>
  cart?.order?.selections?.filter(
    (selection) => selection?.name !== ECOMM_ITEM_NAME
  )

// Sums quantity of checkout eligible items in the cart
export const getCartQuantity = (cart?: Cart | null): number => {
  return (
    getCheckoutEligibleItems(cart)?.reduce(
      (sum, item) => sum + (item?.quantity ?? 0),
      0
    ) ?? 0
  )
}

export const calculateSubtotalExcludingShipping = (
  order?: CartOrder | CompletedOrder | null,
  shippingItem?: { price?: number | null } | null | undefined
) => {
  const baseSubtotal = order?.preDiscountItemsSubtotal || 0
  const shippingCost = shippingItem?.price || 0
  return Math.max(0, baseSubtotal - shippingCost)
}

export const calculateSubtotalExcludingShippingFromCart = (
  cart?: Cart | null
) => {
  return calculateSubtotalExcludingShipping(
    cart?.order,
    getEcommShippingFee(cart)
  )
}

/**
 * Determines the fulfillment type of a completed order
 * @param completedOrder - The completed order to analyze
 * @returns 'shipping' | 'delivery' | 'pickup'
 */
export const getEcommFulfillmentType = (completedOrder: CompletedOrder) => {
  // Check if this is a shipping order by looking for the shipping fee selection
  const isShippingOrder = completedOrder.selections?.some(
    (selection) => selection?.name === ECOMM_ITEM_NAME
  )

  if (isShippingOrder) {
    return 'shipping'
  }

  // Check if this is a delivery order
  if (completedOrder.deliveryInfo) {
    return 'delivery'
  }

  // Default to pickup
  return 'pickup'
}

export enum ItemSource {
  Menu = 'Menu',
  Upsell = 'Upsell',
  UpsellModal = 'Upsell Modal',
  CartOffer = 'Cart Offer',
  Reorder = 'Reorder',
  Search = 'Search',
  Cart = 'Cart',
  CartQuantitySelector = 'Cart Quantity Selector',
  Checkout = 'Checkout'
}

export enum EditSource {
  CartQuantitySelector = 'Cart Quantity Selector',
  Checkout = 'Checkout',
  ItemModal = 'Item Modal',
  QuickAddToCart = 'Quick Add to Cart'
}

export const wrappedModifier = (item: MenuItem): WrappedModifier => {
  return {
    groupGuid: item.itemGroupGuid,
    modifier: {
      itemGuid: item.guid || '',
      itemGroupGuid: item.itemGroupGuid,
      modifierGroups: [],
      price: item.prices?.[0] || item.price || 0
    },
    modifierGroups: {},
    isRoot: true,
    masterId: item.masterId,
    action: 'replace' as const,
    price: item.prices?.[0] || item.price || 0,
    usesFractionalQuantity: item.usesFractionalQuantity,
    unitOfMeasure: item.unitOfMeasure,
    name: item.name
  }
}
